import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {EntityTypes, Parameters, ParameterService, PropertyLineModel, SpecLineModel, StateService} from "web-shared";
import {AppConfig, BaseComponent, FilterModel, GLOBAL, IdTextValueModel} from "common-shared";
import {instanceToInstance, plainToInstance} from "class-transformer";
import {takeUntil} from "rxjs/operators";
import {Router} from "@angular/router";
import {ResourceService} from "../../../manufacturing/resources/resource.service";

interface SavedFilterModel {
  Id: string;
  Name: string;
  Value: any;
  Type: number;
}

@Component({
  selector: 'app-saved-filters',
  templateUrl: './saved-filters.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SavedFiltersComponent extends BaseComponent implements OnInit, OnChanges {
  savedFilters: SavedFilterModel[] = [];
  sharedFilters: SavedFilterModel[] = [];
  hasSavedFilters: boolean;
  hasSharedFilters: boolean;
  editableFilter: SavedFilterModel;
  readonly imeType: number = 100;
  @Input() specs: SpecLineModel[];
  filterTypes = [
    {Key: GLOBAL.Properties.ImeListFilterSettings, Value: this.imeType},
    {Key: GLOBAL.Properties.ItemListFilterSettings, Value: EntityTypes.Item},
    {Key: GLOBAL.Properties.ItemGroupListFilterSettings, Value: EntityTypes.ItemGroup},
    {Key: GLOBAL.Properties.ResourceListFilterSettings, Value: EntityTypes.Resource},
    {Key: GLOBAL.Properties.ConfigurationListFilterSettings, Value: EntityTypes.Configuration},
  ];
  @Input() customTypes: { Key: string, Value: EntityTypes }[] = [];

  constructor(@Inject('config') public appConfig: AppConfig, private parameterService: ParameterService, private resourceService: ResourceService, private changeDetector: ChangeDetectorRef, private stateService: StateService, private router: Router) {
    super();
  }

  ngOnInit(): void {
    this.prepareSharedFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.specs?.currentValue) {
      this.prepareSavedFilters();
    }
  }

  @HostListener('document:keydown.esc', ['$event'])
  cancel() {
    this.editableFilter = null;
  }

  private prepareSavedFilters() {
    this.savedFilters = [];

    if (this.specs) {
      [...this.filterTypes, ...this.customTypes].forEach(type => {
        this.addSavedFilters(this.specs.find(spec => spec.PropertyId === type.Key), type.Value);
      });
      this.hasSavedFilters = this.savedFilters.length > 0;
      this.changeDetector.detectChanges();
    }
  }

  private prepareSharedFilters() {
    this.sharedFilters = [];
    this.parameterService.getParameter(Parameters.SharedFilters).pipe(takeUntil(this.componentDestroyed)).subscribe(sharedFilterParameter => {
      if (sharedFilterParameter) {
        const filters = JSON.parse(sharedFilterParameter.Value);
        for (let filter of filters) {
          this.sharedFilters.push(filter);
        }
      }

      this.hasSharedFilters = this.sharedFilters.length > 0;
      this.changeDetector.detectChanges();
    });
  }

  private addSavedFilters(settingsSpec: SpecLineModel, type: number) {
    if (settingsSpec) {
      const settings = plainToInstance(IdTextValueModel, <string[]>JSON.parse(settingsSpec.Value));
      settings.forEach(filter => {
        this.savedFilters.push({Id: filter.Id, Name: filter.Text, Value: filter.Value, Type: type});
      });
    }
  }

  edit(filter: SavedFilterModel) {
    this.editableFilter = instanceToInstance(filter);
  }

  delete() {
    const propertyId = this.getFilterSettingsPropertyId(this.editableFilter.Type);
    const specLineModel = this.specs.find(spec => spec.PropertyId === propertyId);
    let settings = plainToInstance(IdTextValueModel, <string[]>JSON.parse(specLineModel.Value));
    settings = settings.filter(setting => setting.Id !== this.editableFilter.Id);
    let propertyLine = new PropertyLineModel();
    propertyLine.Id = specLineModel.LineId
    propertyLine.PropertyId = specLineModel.PropertyId;
    propertyLine.Value = JSON.stringify(settings);
    this.resourceService.editPropertyLine(propertyLine).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      this.savedFilters = this.savedFilters.filter(filter => filter.Id !== this.editableFilter.Id);
      specLineModel.Value = JSON.stringify(settings);
      this.updateSpecs();
    });
  }

  deleteShared() {
    this.parameterService.getParameter(Parameters.SharedFilters).pipe(takeUntil(this.componentDestroyed)).subscribe(sharedFilterParameter => {
      const sharedFilters: SavedFilterModel[] = JSON.parse(sharedFilterParameter.Value);
      const filteredData = sharedFilters.filter(item => item.Id !== this.editableFilter.Id);
      sharedFilterParameter.Value = JSON.stringify(filteredData);
      this.parameterService.save(sharedFilterParameter).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
        this.prepareSharedFilters()
      });
    });
  }

  save() {
    const propertyId = this.getFilterSettingsPropertyId(this.editableFilter.Type);
    const specLineModel = this.specs.find(spec => spec.PropertyId === propertyId);
    const settings = plainToInstance(IdTextValueModel, <string[]>JSON.parse(specLineModel.Value));
    const modifiableFilter = settings.find(setting => setting.Id === this.editableFilter.Id);
    modifiableFilter.Text = this.editableFilter.Name;
    let propertyLine = new PropertyLineModel();
    propertyLine.Id = specLineModel.LineId
    propertyLine.PropertyId = specLineModel.PropertyId;
    propertyLine.Value = JSON.stringify(settings);
    this.resourceService.editPropertyLine(propertyLine).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      this.savedFilters.map(filter => {
        if (filter.Id === this.editableFilter.Id) {
          filter.Name = this.editableFilter.Name;
        }
      });
      this.updateSpecs();
    });
  }

  saveShared() {
    this.parameterService.getParameter(Parameters.SharedFilters).pipe(takeUntil(this.componentDestroyed)).subscribe(sharedFilterParameter => {
      const sharedFilters: SavedFilterModel[] = JSON.parse(sharedFilterParameter.Value);
      const editableFilter = sharedFilters.find(filter => filter.Id === this.editableFilter.Id);
      if (editableFilter) {
        editableFilter.Name = this.editableFilter.Name;
      }
      sharedFilterParameter.Value = JSON.stringify(sharedFilters);

      this.parameterService.save(sharedFilterParameter).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
        this.updateSpecs();
        this.prepareSharedFilters();
      });
    });
  }

  share() {
    this.parameterService.getParameter(Parameters.SharedFilters).pipe(takeUntil(this.componentDestroyed)).subscribe(sharedFilterParameter => {
      const sharedFilters: SavedFilterModel[] = JSON.parse(sharedFilterParameter.Value);
      let filterAlreadySaved = sharedFilters.find((filter: { Id: string; }) => filter.Id === this.editableFilter.Id);
      if (!filterAlreadySaved) {
        sharedFilters.push(this.editableFilter);
      }
      sharedFilterParameter.Value = JSON.stringify(sharedFilters);

      this.parameterService.save(sharedFilterParameter).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
        this.delete();
        this.prepareSharedFilters();
      });
    });
  }

  getFilterSettingsPropertyId(type: number): string {
    return [...this.filterTypes, ...this.customTypes].find(filterType => filterType.Value === type)?.Key;
  }

  open(filter: SavedFilterModel) {
    const state = JSON.parse(filter.Value);
    let namespace = state.Route;
    if (!namespace) {
      switch (filter.Type) {
        case EntityTypes.Item: {
          namespace = '/manufacturing/item';
          break;
        }
        case EntityTypes.ItemGroup: {
          namespace = '/manufacturing/item-template';
          break;
        }
        case EntityTypes.Resource: {
          namespace = '/manufacturing/resource';
          break;
        }
        case EntityTypes.Configuration: {
          namespace = '/manufacturing/configuration';
          break;
        }
        case this.imeType: {
          namespace = '/manufacturing/ime';
          break;
        }
      }
    }

    if (state.StateKey) {
      if (this.appConfig.primeNgStateStorage === "session") {
        sessionStorage.setItem(state.StateKey, state.Filter);
      } else {
        localStorage.setItem(state.StateKey, state.Filter);
      }
    } else {
      this.stateService.setFilterState(plainToInstance(FilterModel, JSON.parse(filter.Value)), namespace);
    }
    this.router.navigate([namespace]).catch(reason => console.error(reason));
  }

  private updateSpecs() {
    let currentUser = this.stateService.currentUser$.value;
    this.resourceService.getCurrentUserSpecs().pipe(takeUntil(this.componentDestroyed)).subscribe(specs => {
      currentUser.Specs = specs;
      this.stateService.currentUser$.next(currentUser);
      this.editableFilter = null;
      this.changeDetector.detectChanges();
    });
  }
}
